import { useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  SkeletonBlock,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { useDrawer } from '@/app/providers';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData } from '@/versions/v1/shared/useOemData';
import type {
  CustomerSatisfactionResponse,
  RosterEntry,
  WorkOrder,
} from '@/versions/v1/shared/useOemData';
import { useLakeDoc } from '@/versions/v1/shared/lake-drawer';
import { askOem } from '@/versions/v1/shared/assistant/ask';
import { rememberLoop, routeForView } from '@/versions/v1/shared/oem-routes';
import { LOOPS, LOOP_BY_ID, type LoopId } from '@/versions/v1/shared/seed/loops';
import {
  MODULE_STATUS_TOKENS,
  type ModuleStatus,
  type SeedNav,
} from '@/versions/v1/shared/seed/modules';
import { BandPill, MeasureTile, SceneSection, SourceLink } from '@/versions/v1/shared/oem-tiles';
import { iconForLoop } from './loop-icons';
import {
  auditSlice,
  bulletinChips,
  figureText,
  loopFigureValues,
  loopHeroStats,
  loopTablePanels,
  siblingLoops,
  type LoopCell,
  type LoopCtx,
} from './LoopScene.transform';

// =========================================================================
// One feedback loop. Route `/loops/:loopId`. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `renderLoops` (source line 3034). The page is three
// bands, in the demo's order:
//
//   1. hero — loop number, owning agent, name, purpose, four live figures
//   2. "what this loop does · where it lives · the live figure" — the item
//      list, every row navigable
//   3. the loop's own panels — a table per `LOOPS[i].table()`
//
// then the sibling-loop footer.
//
// Every one of the 27 live figures in `seed/loops.ts` is bound to the engine
// in the sibling transform, and seven of the eight panels are rebuilt from the
// loaded data. The eighth is the bulletin chip row, which lives here because
// each chip opens a lake document through `useLakeDoc`.
//
// UI layer.
// =========================================================================

/** The six lazy domains the five loop pages read between them. */
function useLoopData(): { ctx: LoopCtx | null; loading: boolean; error: Error | null } {
  const workOrders = useOemData('work-orders');
  const parts = useOemData('parts');
  const people = useOemData('people');
  const service = useOemData('service');
  const comms = useOemData('comms');
  const costs = useOemData('costs');

  const loading =
    workOrders.loading ||
    parts.loading ||
    people.loading ||
    service.loading ||
    comms.loading ||
    costs.loading;

  const error =
    workOrders.error ??
    parts.error ??
    people.error ??
    service.error ??
    comms.error ??
    costs.error ??
    null;

  const ctx = useMemo<LoopCtx | null>(() => {
    if (
      !workOrders.data ||
      !parts.data ||
      !people.data ||
      !service.data ||
      !comms.data ||
      !costs.data
    ) {
      return null;
    }
    return {
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
      kb: service.data.kb,
      training: service.data.lms,
      contracts: service.data.mx_contracts,
      bulletins: parts.data.bulletins,
      inventory: parts.data.inventory,
      orders: parts.data.orders,
      parts: parts.data.parts,
      roster: unpackTable<RosterEntry>(people.data.roster),
      techs: people.data.tapp_techs,
      css: unpackTable<CustomerSatisfactionResponse>(people.data.css),
      timesheets: costs.data.timesheets,
      meetings: comms.data.meetings,
    };
  }, [workOrders.data, parts.data, people.data, service.data, comms.data, costs.data]);

  return { ctx, loading, error };
}

/** Delivery-status pill — the demo's `ST` tokens as colour. */
const STATUS_COLOR: Readonly<Record<ModuleStatus, string>> = {
  live: 'var(--good)',
  drafts: 'var(--accent)',
  worked: 'var(--accent)',
  road: 'var(--ink-tertiary)',
};

function StatusPill({ status }: { status: ModuleStatus }) {
  const token = MODULE_STATUS_TOKENS[status];
  const color = STATUS_COLOR[status];
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10.5px] font-medium leading-[1.6]"
      style={{ background: `color-mix(in srgb, ${color} 14%, transparent)`, color }}
    >
      {token.label}
    </span>
  );
}

export function LoopScene() {
  const { loopId = '' } = useParams<{ loopId: string }>();
  const navigate = useNavigate();
  const { open: openDrawer } = useDrawer();
  const openLakeDoc = useLakeDoc();
  const { ctx, loading, error } = useLoopData();

  const loop = LOOP_BY_ID[loopId as LoopId];

  // `show('loops')` with no id reopens the last loop on screen (demo LOOP_CUR).
  useEffect(() => {
    if (loop) rememberLoop(loop.id);
  }, [loop]);

  const values = useMemo(() => (ctx ? loopFigureValues(ctx) : null), [ctx]);
  const heroStats = useMemo(
    () => (loop ? loopHeroStats(loop, values) : []),
    [loop, values],
  );
  const panels = useMemo(
    () => (loop && ctx ? loopTablePanels(loop, ctx) : []),
    [loop, ctx],
  );
  const chips = useMemo(() => (ctx ? bulletinChips(ctx.bulletins) : []), [ctx]);
  const siblings = useMemo(() => (loop ? siblingLoops(loop, LOOPS) : []), [loop]);

  /** One item row's `SeedNav`, resolved. The four kinds are the demo's four. */
  const follow = useCallback(
    (nav: SeedNav) => {
      if (nav.kind === 'ask') {
        askOem(nav.question);
        return;
      }
      if (nav.kind === 'openDoc') {
        const [, file] = nav.key.split(/:(.+)/);
        openLakeDoc({ file, connector: 'Teams · meeting transcript' });
        return;
      }
      if (nav.kind === 'audit') {
        if (!ctx) return;
        const payload = auditSlice(nav.table, nav.filter, ctx);
        if (!payload) return;
        openDrawer('oem-source', {
          title: payload.title,
          wide: true,
          data: payload as unknown as Record<string, unknown>,
        });
        return;
      }
      const to = routeForView(nav.action.view, nav.action.sub);
      if (to) navigate(to);
    },
    [ctx, navigate, openDrawer, openLakeDoc],
  );

  const openMachine = useCallback(
    (id: string) => {
      const to = routeForView('machine', id);
      if (to) navigate(to);
    },
    [navigate],
  );

  if (!loop) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody className="max-w-[1240px] pt-6">
          <EmptyState
            icon={<ArrowLeft className="h-8 w-8 text-zinc-400" aria-hidden="true" />}
            title="No such feedback loop"
            description={`"${loopId}" is not one of the five loops. Go back to the section landing to pick one.`}
          />
          <p className="mt-4">
            <Link to="/loops" className="text-[12.5px]" style={{ color: 'var(--accent)' }}>
              All five loops ›
            </Link>
          </p>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const Icon = iconForLoop(loop.id);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="LoopScene">
        <SurfacePageBody className="@container max-w-[1240px] space-y-8 pt-6">
          <SurfacePageHeader
            kicker={`Feedback loops · loop ${loop.n} of ${LOOPS.length}`}
            title={loop.name}
            description={loop.purpose}
            actions={<HelpAnchor doc="loops/loop-page" />}
            meta={
              <span className="flex flex-wrap items-center gap-2 text-[11.5px]">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-md"
                  style={{
                    background: `color-mix(in srgb, ${loop.col} 14%, transparent)`,
                    color: loop.col,
                  }}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span style={{ color: 'var(--ink-tertiary)' }}>run by</span>
                <SourceLink onClick={() => navigate(`/agents?agent=${encodeURIComponent(loop.agent)}`)}>
                  {`${loop.agent} agent`}
                </SourceLink>
              </span>
            }
          />

          <AskStrip view="loops" />

          {error ? (
            <div
              role="alert"
              className="rounded-xl border p-4 text-sm"
              style={{
                borderColor: 'var(--hairline)',
                background: 'var(--crit-soft)',
                color: 'var(--crit)',
              }}
            >
              {`The loop's live figures could not be loaded (${error.message}). The rows below are the
              operating model; the figures beside them are the 09 Sep 2026 snapshot and are blank
              rather than guessed.`}
            </div>
          ) : null}

          {/* --------------------------------------------------------- */}
          {/* Hero figures                                              */}
          {/* --------------------------------------------------------- */}
          <Commentable name="Hero">
            {heroStats.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
                {heroStats.map((stat) => (
                  <MeasureTile
                    key={stat.eyebrow}
                    eyebrow={stat.eyebrow}
                    value={stat.value}
                    detail={stat.detail}
                  />
                ))}
              </div>
            ) : (
              <SkeletonCardGrid count={4} cols={2} />
            )}
          </Commentable>

          {/* --------------------------------------------------------- */}
          {/* Item list                                                 */}
          {/* --------------------------------------------------------- */}
          <Commentable name="Items">
            <SceneSection
              title="What this loop does"
              subtitle="where it lives · the live figure"
            >
              <ul
                className="grid list-none grid-cols-1 gap-px overflow-hidden rounded-xl border p-0"
                style={{ borderColor: 'var(--hairline)', background: 'var(--hairline)' }}
              >
                {loop.items.map((item) => (
                  <li key={item.what} style={{ background: 'var(--surface-2)' }}>
                    <button
                      type="button"
                      onClick={() => follow(item.nav)}
                      className="grid w-full grid-cols-1 items-start gap-x-4 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)] @3xl:grid-cols-[minmax(0,15rem)_minmax(0,17rem)_minmax(0,1fr)_auto]"
                    >
                      <span
                        className="text-[12.5px] font-medium leading-[1.45]"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {item.what}
                      </span>
                      <span
                        className="text-[11.5px] leading-[1.5]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        {item.where}
                      </span>
                      <span
                        className="text-[12px] leading-[1.5]"
                        style={{ color: 'var(--ink-secondary)' }}
                      >
                        {figureText(item.figure, values)}
                      </span>
                      <StatusPill status={item.status} />
                    </button>
                  </li>
                ))}
              </ul>
            </SceneSection>
          </Commentable>

          {/* --------------------------------------------------------- */}
          {/* Bulletin chips — loop 2 only                              */}
          {/* --------------------------------------------------------- */}
          {loop.panels.some((p) => p.kind === 'chips') ? (
            <Commentable name="Bulletins">
              <SceneSection
                title="Bulletins in force"
                subtitle="click to open the source document"
              >
                {!ctx ? (
                  <SkeletonBlock className="h-24 rounded-xl" />
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {chips.map((chip) => (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() =>
                          openLakeDoc({
                            file: chip.file,
                            connector: 'SharePoint · OEM bulletin service',
                            title: `${chip.id} · ${chip.label}`,
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-left text-[11.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                        style={{
                          borderColor: 'var(--hairline)',
                          background: 'var(--surface-2)',
                          color: 'var(--ink-secondary)',
                        }}
                      >
                        <span className="mono" style={{ color: 'var(--ink-primary)' }}>
                          {chip.id}
                        </span>
                        <span>{chip.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </SceneSection>
            </Commentable>
          ) : null}

          {/* --------------------------------------------------------- */}
          {/* Panels                                                    */}
          {/* --------------------------------------------------------- */}
          <Commentable name="Panels">
            {!ctx ? (
              <SkeletonBlock className="h-64 rounded-xl" />
            ) : (
              <div className="space-y-8">
                {panels.map((panel) => (
                  <SceneSection key={panel.eyebrow} title={panel.eyebrow}>
                    <div
                      className="overflow-hidden rounded-xl border"
                      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
                    >
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[48rem] border-collapse text-left">
                          <caption className="sr-only">{panel.eyebrow}</caption>
                          <thead>
                            <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                              {panel.cols.map((col) => (
                                <th
                                  key={col.label}
                                  scope="col"
                                  className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${
                                    col.numeric ? 'text-right' : ''
                                  }`}
                                  style={{ color: 'var(--ink-tertiary)' }}
                                >
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {panel.rows.map((row, i) => (
                              <tr
                                key={`${panel.eyebrow}-${row[0]?.text ?? i}-${i}`}
                                style={{ borderTop: '1px solid var(--hairline)' }}
                              >
                                {row.map((cell, j) => (
                                  <td
                                    key={panel.cols[j]?.label ?? String(j)}
                                    className={cellClass(cell)}
                                    style={{ color: cellColor(cell) }}
                                  >
                                    <Cell cell={cell} onMachine={openMachine} />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {panel.rows.length === 0 ? (
                      <p
                        className="mt-3 text-[11.5px]"
                        style={{ color: 'var(--ink-tertiary)' }}
                      >
                        Nothing matches this morning.
                      </p>
                    ) : null}
                  </SceneSection>
                ))}
              </div>
            )}
          </Commentable>

          {/* --------------------------------------------------------- */}
          {/* Sibling loops                                             */}
          {/* --------------------------------------------------------- */}
          <Commentable name="Siblings">
            <SceneSection
              title="The other loops"
              subtitle="each one feeds the front of the operating model a different way"
            >
              <div className="flex flex-wrap gap-1.5">
                {siblings.map((sibling) => (
                  <Link
                    key={sibling.id}
                    to={`/loops/${sibling.id}`}
                    className="inline-flex items-center rounded-full border px-3 py-1.5 text-[12px] no-underline transition-colors hover:bg-[var(--surface-1)]"
                    style={{
                      borderColor: 'var(--hairline)',
                      background: 'var(--surface-2)',
                      color: 'var(--ink-secondary)',
                    }}
                  >
                    {`${sibling.name} ›`}
                  </Link>
                ))}
                <Link
                  to="/loops"
                  className="inline-flex items-center rounded-full border px-3 py-1.5 text-[12px] no-underline transition-colors hover:bg-[var(--surface-1)]"
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--surface-2)',
                    color: 'var(--accent)',
                  }}
                >
                  All five loops ›
                </Link>
              </div>
            </SceneSection>
          </Commentable>

          <p className="sr-only" role="status">
            {loading && !ctx
              ? `Loading the live figures for the ${loop.name} loop.`
              : `${loop.items.length} things this loop does.`}
          </p>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/** Table cell body. A machine id is a link; everything else is text. */
function Cell({ cell, onMachine }: { cell: LoopCell; onMachine: (id: string) => void }) {
  if (cell.kind === 'machine') {
    return (
      <span className="flex flex-wrap items-baseline gap-1.5">
        <SourceLink onClick={() => onMachine(cell.key ?? cell.text)} className="mono !text-[12px]">
          {cell.text}
        </SourceLink>
        {cell.sub ? (
          <span className="text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
            {cell.sub}
          </span>
        ) : null}
      </span>
    );
  }

  if (cell.kind === 'band') {
    return <BandPill band={cell.band ?? 'fair'}>{cell.text}</BandPill>;
  }

  return (
    <span className="block">
      <span className="block">{cell.text}</span>
      {cell.sub ? (
        <span className="mt-0.5 block text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {cell.sub}
        </span>
      ) : null}
    </span>
  );
}

function cellClass(cell: LoopCell): string {
  const base = 'px-4 py-2.5 align-top text-[12px] leading-[1.5]';
  if (cell.kind === 'numeric') return `${base} mono whitespace-nowrap text-right`;
  if (cell.kind === 'mono' || cell.kind === 'part') return `${base} mono whitespace-nowrap`;
  return base;
}

function cellColor(cell: LoopCell): string {
  if (cell.critical) return 'var(--crit)';
  if (cell.kind === 'mono' || cell.kind === 'part' || cell.kind === 'numeric') {
    return 'var(--ink-primary)';
  }
  return 'var(--ink-secondary)';
}

export default LoopScene;
